import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchJourneyStepFieldMapping,
  type AudiencePreview,
  type CampaignRecipientPlan,
  type FieldMappingRow,
  type JourneyStepFieldMapping,
  type JourneyStepTemplateFieldMappings,
  type OurSourceField,
  type TemplateFieldMappingRule,
} from "./campaignBuilderApi";
import { plannedRecipientsForEmailStep } from "./plannedRecipientsForStep";
import { CampaignContactLink, type CampaignCrmNavigation } from "./CrmRecordLinks";

const PREVIEW_PRELIM_ROWS = 25;
const DEFAULT_SEND_FROM = "Ironfleet<noreply@ironfleet.ai>";

function coverageClass(percent: number): string {
  if (percent >= 90) return "field-mapping-coverage-good";
  if (percent >= 50) return "field-mapping-coverage-mid";
  return "field-mapping-coverage-low";
}

function effectiveRule(
  row: FieldMappingRow,
  saved: JourneyStepTemplateFieldMappings,
): TemplateFieldMappingRule {
  const fromSaved = saved.rules[row.target];
  if (fromSaved) return fromSaved;
  return {
    mode: row.mode ?? "source",
    source_path: row.source_path ?? row.recommended_source,
    fixed_field_id: row.fixed_field_id ?? null,
    fixed_value: row.fixed_value ?? null,
  };
}

function sourceLabel(path: string | null | undefined, fields: OurSourceField[], row: FieldMappingRow): string {
  if (!path) return row.source_label ?? "Auto";
  const match = fields.find((f) => f.path === path);
  return match ? `${match.label} (${path})` : path;
}

function contactName(c: AudiencePreview["qualified"][number]): string {
  const parts = [c.first_name, c.last_name].filter(Boolean);
  return parts.length ? parts.join(" ") : c.email ?? c.contact_id;
}

export function SendPreviewModal({
  stepId,
  stepName,
  sendFrom,
  audienceFilter,
  campaignKey,
  audience,
  recipientPlan,
  busy,
  onConfirm,
  onClose,
  crmNavigation,
}: {
  stepId: string;
  stepName: string;
  sendFrom?: string;
  audienceFilter: Record<string, unknown>;
  campaignKey?: string | null;
  audience: AudiencePreview;
  recipientPlan: CampaignRecipientPlan | null;
  busy: boolean;
  onConfirm: (sendFrom: string) => void;
  onClose: () => void;
  crmNavigation?: CampaignCrmNavigation;
}) {
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingError, setMappingError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<JourneyStepFieldMapping | null>(null);
  const [showAllPreview, setShowAllPreview] = useState(false);
  const [sendFromDraft, setSendFromDraft] = useState(sendFrom ?? "");

  const recipients = useMemo(
    () => plannedRecipientsForEmailStep(stepId, recipientPlan, audience),
    [stepId, recipientPlan, audience],
  );

  const loadMapping = useCallback(
    async (previewLimit: number) => {
      setMappingLoading(true);
      setMappingError(null);
      try {
        const result = await fetchJourneyStepFieldMapping(stepId, audienceFilter, campaignKey ?? undefined, {
          audience_preview_limit: previewLimit,
        });
        setMapping(result);
      } catch (err: unknown) {
        setMappingError(err instanceof Error ? err.message : "Could not load send preview");
        setMapping(null);
      } finally {
        setMappingLoading(false);
      }
    },
    [stepId, audienceFilter, campaignKey],
  );

  useEffect(() => {
    const limit = showAllPreview ? 0 : PREVIEW_PRELIM_ROWS;
    void loadMapping(limit);
  }, [loadMapping, showAllPreview]);

  useEffect(() => {
    setSendFromDraft(sendFrom ?? "");
  }, [sendFrom, stepId]);

  useEffect(() => {
    if (!mapping) return;
    if (mapping.send_from != null) {
      setSendFromDraft(mapping.send_from);
    }
  }, [mapping]);

  const saved: JourneyStepTemplateFieldMappings = mapping?.saved_mappings ?? { version: 1, rules: {} };
  const fixedRows = useMemo(() => {
    if (!mapping) return [];
    return mapping.mappings.filter((row) => (effectiveRule(row, saved).mode ?? "source") === "fixed");
  }, [mapping, saved]);

  const audienceRows = useMemo(() => {
    if (!mapping) return [];
    return mapping.mappings.filter((row) => (effectiveRule(row, saved).mode ?? "source") === "source");
  }, [mapping, saved]);

  const audienceTargets = mapping?.audience_sourced_targets ?? [];
  const gridRows = mapping?.audience_preview ?? [];

  return (
    <div
      className="modal-backdrop field-mapping-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="send-preview-title"
    >
      <div className="modal-card field-mapping-modal audience-send-modal">
        <div className="field-mapping-modal-head">
          <div>
            <h3 id="send-preview-title">Send to audience — {stepName}</h3>
            <p className="hint field-mapping-modal-sub">
              Only contacts still planned for this email are sent. Staged contacts were already triggered and are left
              off this run. Review fixed values and field mappings before bulk send.
            </p>
          </div>
        </div>

        {mappingError ? (
          <p className="hint test-send-mapping-error" role="alert">{mappingError}</p>
        ) : null}

        <label className="campaign-builder-field field-mapping-send-from">
          <span>Send from</span>
          <input
            type="text"
            value={sendFromDraft}
            onChange={(e) => setSendFromDraft(e.target.value)}
            placeholder={DEFAULT_SEND_FROM}
            disabled={busy}
          />
          <span className="hint">Use <code>Name&lt;email&gt;</code> or plain email.</span>
        </label>

        <div className="field-mapping-modal-body audience-send-modal-body">
          <section className="audience-send-section">
            <h4>Fixed fields</h4>
            <p className="hint">Same value for every recipient in this send.</p>
            {mappingLoading && !mapping ? (
              <p className="hint">Loading field configuration…</p>
            ) : fixedRows.length ? (
              <div className="field-mapping-rules-panel">
                <table className="rating-table field-mapping-rules-table audience-send-readonly-table">
                  <thead>
                    <tr>
                      <th>SendGrid field</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fixedRows.map((row) => {
                      const rule = effectiveRule(row, saved);
                      const catalog = mapping?.fixed_fields.find((f) => f.id === rule.fixed_field_id);
                      const value =
                        (rule.fixed_value ?? "").trim() ||
                        catalog?.default_value ||
                        row.fixed_value ||
                        "—";
                      return (
                        <tr key={row.target}>
                          <td><code>{row.target}</code></td>
                          <td>{value}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="hint audience-send-empty">No fixed template fields for this email.</p>
            )}
          </section>

          <section className="audience-send-section audience-send-recipients-section">
            <h4>Planned for this email</h4>
            <p className="hint">
              <strong>{recipients.length}</strong> planned recipient{recipients.length === 1 ? "" : "s"} in the locked
              audience
            </p>
            {recipients.length ? (
              <div className="audience-send-recipients-scroll">
                <table className="rating-table field-mapping-rules-table audience-send-readonly-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Title</th>
                      <th>Segment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((c) => (
                      <tr key={c.contact_id}>
                        <td>
                          <CampaignContactLink
                            contactId={c.contact_id}
                            label={contactName(c)}
                            navigation={crmNavigation}
                          />
                        </td>
                        <td>{c.email}</td>
                        <td>{c.title?.trim() || "—"}</td>
                        <td>{c.company_segment?.trim() || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="hint audience-send-empty">
                No contacts are still planned for this email. Add new contacts in the audience builder, or everyone
                already assigned has been staged.
              </p>
            )}
          </section>

          <section className="audience-send-section audience-send-mappings-section">
            <h4>Audience field mappings</h4>
            {mappingLoading && !mapping ? (
              <p className="hint">Loading mappings…</p>
            ) : audienceRows.length ? (
              <>
                <div className="field-mapping-rules-panel">
                  <table className="rating-table field-mapping-rules-table audience-send-readonly-table">
                    <thead>
                      <tr>
                        <th>SendGrid field</th>
                        <th>Audience source</th>
                        <th>Coverage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {audienceRows.map((row) => {
                        const rule = effectiveRule(row, saved);
                        const path = rule.source_path ?? row.recommended_source;
                        return (
                          <tr key={row.target}>
                            <td><code>{row.target}</code></td>
                            <td>{sourceLabel(path, mapping?.source_fields ?? [], row)}</td>
                            <td>
                              <span className={`field-mapping-coverage ${coverageClass(row.coverage.percent)}`}>
                                {row.coverage.populated}/{row.coverage.total} ({row.coverage.percent}%)
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {audienceTargets.length ? (
                  <div className="audience-send-preview-block">
                    <p className="hint audience-send-preview-caption">
                      Per-recipient values for audience-sourced fields · showing {gridRows.length} of{" "}
                      {mapping?.universe_total ?? recipients.length}
                      {mappingLoading ? " · updating…" : ""}
                    </p>
                    {mapping && mapping.universe_total > PREVIEW_PRELIM_ROWS ? (
                      <label className="field-mapping-show-all">
                        <input
                          type="checkbox"
                          checked={showAllPreview}
                          disabled={mappingLoading}
                          onChange={(e) => setShowAllPreview(e.target.checked)}
                        />
                        Show all contacts in preview
                      </label>
                    ) : null}
                    <div className="field-mapping-audience-scroll audience-send-grid-scroll">
                      <table className="rating-table field-mapping-audience-grid">
                        <thead>
                          <tr>
                            <th className="field-mapping-sticky-col">Contact</th>
                            <th className="field-mapping-sticky-col">Email</th>
                            {audienceTargets.map((target) => (
                              <th key={target}><code>{target}</code></th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {gridRows.map((row) => (
                            <tr key={row.contact_id}>
                              <td className="field-mapping-sticky-col">
                                <CampaignContactLink
                                  contactId={row.contact_id}
                                  label={row.name || "—"}
                                  navigation={crmNavigation}
                                />
                              </td>
                              <td className="field-mapping-sticky-col">{row.email}</td>
                              {audienceTargets.map((target) => (
                                <td key={target} className="field-mapping-grid-cell">
                                  {row.cells[target] ?? "—"}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {mapping?.audience_preview_truncated && !showAllPreview ? (
                      <p className="hint">Preview truncated — enable show all to load full audience.</p>
                    ) : null}
                  </div>
                ) : (
                  <p className="hint audience-send-empty">No audience-sourced fields to preview.</p>
                )}
              </>
            ) : (
              <p className="hint audience-send-empty">No audience-mapped template fields for this email.</p>
            )}
          </section>
        </div>

        <div className="campaign-builder-actions field-mapping-modal-actions audience-send-modal-actions">
          <button
            type="button"
            className="audience-send-now-btn"
            disabled={busy || recipients.length === 0 || mappingLoading}
            onClick={() => onConfirm(sendFromDraft.trim())}
          >
            {busy ? "Sending…" : "Send now"}
          </button>
          <button type="button" className="secondary" disabled={busy} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
